import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useOrganizationSectionVisibility } from "src/pages/Product/hooks/useOrganizationSectionVisibility";
import { useProductLifecycleStepGate } from "src/pages/Product/hooks/useProductLifecycleStepGate";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

export type WizardStepKey =
    | "identity"
    | "additionalInformations"
    | "organization"
    | "recipe"
    | "transformation"
    | "packaging"
    | "transport"
    | "distribution"
    | "consumption";

/**
 * Transformation, distribution and consumption are gated by application-parameter flags
 * and org-scoped product permissions.
 * Additional informations is gated by the presence of admin-defined JSON attributes
 * for the Product entity.
 */
type WizardStepDescriptor = {
    key: WizardStepKey;
    isMounted: boolean;
};

/**
 * Computes the dynamic list of wizard steps for the v2 product form.
 *
 * The numbering shown in the Stepper (X/N) is derived from the length of the returned
 * list, so the user always sees a contiguous numbering even when steps are filtered out.
 */
export const useWizardStepsV2 = (companyIds: { id: number }): WizardStepKey[] => {
    const { entitiesJsonAttributes } = useFoodpilotContext();
    const {
        isRecipeEnabled,
        isTransformationEnabled,
        isPackagingEnabled,
        isTransportEnabled,
        isDistributionEnabled,
        isConsumptionEnabled,
    } = useProductLifecycleStepGate();
    const { shouldHide: shouldHideOrganizationSection } = useOrganizationSectionVisibility({
        currentCompanyId: companyIds.id,
    });

    const productJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.product] ?? [];
    const hasJsonAttributes = productJsonAttributes.length > 0;

    const descriptors: WizardStepDescriptor[] = [
        { key: "identity", isMounted: true },
        { key: "additionalInformations", isMounted: hasJsonAttributes },
        { key: "organization", isMounted: !shouldHideOrganizationSection },
        { key: "recipe", isMounted: isRecipeEnabled },
        { key: "transformation", isMounted: isTransformationEnabled },
        { key: "packaging", isMounted: isPackagingEnabled },
        { key: "transport", isMounted: isTransportEnabled },
        { key: "distribution", isMounted: isDistributionEnabled },
        { key: "consumption", isMounted: isConsumptionEnabled },
    ];

    return descriptors.filter((d) => d.isMounted).map((d) => d.key);
};
